import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import AuthLogos from "../../../core/common/authLogos";
import { setUser, setEnrollment } from "../../../core/data/redux/slice/user";
import { login } from "../../../service/auth";
import { getUserEnrollment } from "../../../service/enrollment";
import { all_routes } from "../../router/all_routes";
import { flattenPermissions, getLandingRoute } from "../../../utils/rbacHelper";
import { resolveDefaultSessionId } from "../../../utils/sessionDefaults";
import { loadBootstrapData } from "../../../utils/bootstrapSession";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  useEffect(() => {
    localStorage.setItem("menuOpened", "Dashboard");
  }, []);

  const fetchUserEnrollment = async (userId: string, sessionId: string) => {
    try {
      const enrollmentResponse = await getUserEnrollment(userId, sessionId);

      if (enrollmentResponse.success && enrollmentResponse.data) {
        return enrollmentResponse.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const getRedirectRoute = (userRole: string, user: any): string => {
    switch (userRole) {
      case "teacher":
        return all_routes.teacherDashboard;
      case "student":
        return all_routes.studentDashboard;
      case "parent":
        return all_routes.parentDashboard;
      default:
        // Admin/staff roles: land on the first page they can access, so a
        // cashier (no "dashboard.view") isn't bounced to /unauthorized.
        return getLandingRoute(user);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      const loginRes = await login(formData.email, formData.password);
      const permissions = flattenPermissions(loginRes.role.permissions);

      // Supplementary data (systems/sessions/branches) — same shared loader
      // page-load/refresh uses, so the campus dropdown/logo are populated
      // immediately after login instead of only after the next refresh. In
      // its own try/catch so a failure here can't be mistaken for invalid
      // credentials (the outer catch's error message) — the login itself
      // already succeeded by this point.
      let sessions: Awaited<ReturnType<typeof loadBootstrapData>>["sessions"] = [];
      try {
        ({ sessions } = await loadBootstrapData(dispatch, loginRes));
      } catch (bootstrapError) {
        console.error("Failed to load supplementary bootstrap data:", bootstrapError);
      }

      const enrollmentSessionId = resolveDefaultSessionId(sessions);
      let userEnrollment = null;
      if (enrollmentSessionId) {
        userEnrollment = await fetchUserEnrollment(
          loginRes._id,
          enrollmentSessionId
        );
      }

      const userWithEnrollment = {
        ...loginRes,
        permissions,
        enrollment: userEnrollment || undefined,
      };
      dispatch(setUser(userWithEnrollment));
      if (userEnrollment) {
        dispatch(setEnrollment(userEnrollment));
      }
      toast.success("Login successful");
      const userRole = loginRes.role?.name?.toLowerCase();
      const redirectRoute = getRedirectRoute(userRole, userWithEnrollment);
      navigate(redirectRoute);
    } catch (error: any) {
      toast.error( "Email or password is invalid!");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const date = () => new Date().getFullYear();

  return (
    <div className="container-fuild">
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-6">
            <div className="login-background d-lg-flex align-items-center justify-content-center d-lg-block d-none flex-wrap vh-100 overflowy-auto">
              <div>
                <ImageWithBasePath
                  src="assets/img/authentication/authentication-02.jpg"
                  alt=""
                />
              </div>
              <div className="authen-overlay-item w-100 p-4">
                <h4 className="text-white mb-3">What's New on Preskool !!!</h4>
                <div className="d-flex align-items-center flex-row mb-3 justify-content-between p-3 br-5 gap-3 card">
                  <div>
                    <h6>Summer Vacation Holiday Homework</h6>
                    <p className="mb-0 text-truncate">
                      The school will remain closed from April 20th to June...
                    </p>
                  </div>
                  <Link to="#">
                    <i className="ti ti-chevrons-right" />
                  </Link>
                </div>
                <div className="d-flex align-items-center flex-row mb-3 justify-content-between p-3 br-5 gap-3 card">
                  <div>
                    <h6>New Academic Session Admission Start(2024-25)</h6>
                    <p className="mb-0 text-truncate">
                      An academic term is a portion of an academic year, the
                      time ....
                    </p>
                  </div>
                  <Link to="#">
                    <i className="ti ti-chevrons-right" />
                  </Link>
                </div>
                <div className="d-flex align-items-center flex-row mb-3 justify-content-between p-3 br-5 gap-3 card">
                  <div>
                    <h6>Date sheet Final Exam Nursery to Sr.Kg</h6>
                    <p className="mb-0 text-truncate">
                      Dear Parents, As the final examination for the session
                      2024-25 is ...
                    </p>
                  </div>
                  <Link to="#">
                    <i className="ti ti-chevrons-right" />
                  </Link>
                </div>
                <div className="d-flex align-items-center flex-row mb-3 justify-content-between p-3 br-5 gap-3 card">
                  <div>
                    <h6>Annual Day Function</h6>
                    <p className="mb-0 text-truncate">
                      Annual functions provide a platform for students to
                      showcase their...
                    </p>
                  </div>
                  <Link to="#">
                    <i className="ti ti-chevrons-right" />
                  </Link>
                </div>
                <div className="d-flex align-items-center flex-row mb-0 justify-content-between p-3 br-5 gap-3 card">
                  <div>
                    <h6>Summer Vacation Holiday Homework</h6>
                    <p className="mb-0 text-truncate">
                      The school will remain closed from April 20th to June 15th
                      for summer...
                    </p>
                  </div>
                  <Link to="#">
                    <i className="ti ti-chevrons-right" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-8 mx-auto p-4">
                <form onSubmit={handleSubmit}>
                  <AuthLogos />
                  <div className="card">
                    <div className="card-body pb-3">
                      <div className="mb-4">
                        <h2 className="mb-2">Welcome</h2>
                        <p className="mb-0">
                          Please enter your details to sign in
                        </p>
                      </div>

                      <div className="mb-3 ">
                        <label className="form-label">Email Address</label>
                        <div className="input-icon mb-3 position-relative">
                          <span className="input-icon-addon">
                            <i className="ti ti-mail" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            name="email"
                            onChange={handleFieldChange}
                            required
                          />
                        </div>

                        <label className="form-label">Password</label>
                        <div className="pass-group">
                          <input
                            name="password"
                            type={isPasswordVisible ? "text" : "password"}
                            className="pass-input form-control"
                            onChange={handleFieldChange}
                            required
                          />
                          <span
                            className={`ti toggle-password ${
                              isPasswordVisible ? "ti-eye" : "ti-eye-off"
                            }`}
                            onClick={togglePasswordVisibility}
                          />
                        </div>
                      </div>

                      <div className="form-wrap form-wrap-checkbox">
                        <div className="d-flex align-items-center">
                          <div className="form-check form-check-md mb-0">
                            <input
                              className="form-check-input mt-0"
                              type="checkbox"
                            />
                          </div>
                          <p className="ms-1 mb-0">Remember Me</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 pt-0">
                      <div className="mb-3">
                        <button
                          className="btn btn-primary w-100"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Logging in..." : "Sign In"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 text-center">
                    <p className="mb-0">
                      Copyright © {date()} - School Management
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
